// Mirror of functions/yearClosing/taxWealthValuation.ts; regenerate with node scripts/sync-tax-wealth.mjs.
(function(global: any) {
// User-documented company wealth basis, separate from income-tax balances.
// Authority: Skatte-ABC 2025/2026 A-4-5.1. No shareholder discount is applied.
const taxWealthCategories = ['fixed', 'inventory', 'investments', 'other', 'debt'] as const
const money = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100
const documented = (value: any) => typeof value === 'string' && value.trim().length > 0 && value.length <= 2000
const canonical = (value: any): any => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])])) : value
const buildTaxWealthValuation = ({ rows = [], scope = {}, year, fixedAssetBasis = {}, valuationFacts = {}, declaration }: any) => {
  const supported = scope.jurisdiction === 'NO' && scope.legalFormCode === 'AS' && [2025, 2026].includes(Number(year))
  const balances = new Map<string, number>()
  for (const row of rows) {
    const code = String(row.code || ''), nr = Number(code.slice(0, 4)), value = Number(row.amount)
    if (!/^\d{4}(?::.+)?$/.test(code) || nr < 1000 || nr > 2999 || (nr >= 2000 && nr <= 2099) || !Number.isFinite(value)) continue
    balances.set(code, money((balances.get(code) || 0) + value))
  }
  const basisRows = [...balances].filter(([, value]) => Math.abs(value) > 0.005).sort(([a], [b]) => a.localeCompare(b))
  const book: Record<string, number> = { fixed: 0, inventory: 0, investments: 0, other: 0, debt: 0 }
  for (const [code, value] of basisRows) {
    const nr = Number(code.slice(0, 4))
    if ([2120, 2500].includes(nr)) continue
    const category = nr < 1300 ? 'fixed' : nr < 1400 || (nr >= 1800 && nr < 1900) ? 'investments' : nr < 1500 ? 'inventory' : nr >= 2100 && value < 0 ? 'debt' : 'other'
    book[category] += category === 'debt' ? -value : value
  }
  for (const key of taxWealthCategories) book[key] = money(book[key])
  // Include tax basis and scope so a prior documented valuation becomes stale
  // even when a tax election changes without changing the financial journal.
  const basis = JSON.stringify({ year: Number(year), jurisdiction: scope.jurisdiction, legalFormCode: scope.legalFormCode,
    framework: scope.accountingFramework || '', rows: basisRows,
    fixedBook: fixedAssetBasis.rmAar ?? null, fixedTax: fixedAssetBasis.smAar ?? null, facts: canonical(valuationFacts) })
  const blockers: string[] = []
  if (!supported) blockers.push('unsupportedScope')
  if (declaration?.basis !== basis) blockers.push('basisChanged')
  if (declaration?.complete !== true) blockers.push('completeness')
  if (!documented(declaration?.reference)) blockers.push('reference')
  const values: Record<string, number> = {}
  for (const key of taxWealthCategories) {
    const entry = declaration?.values?.[key]
    const valid = typeof entry?.amount === 'number' && Number.isSafeInteger(Math.round(entry.amount * 100)) && entry.amount >= 0
    if (!valid || !documented(entry?.note)) blockers.push(`value.${key}`)
    values[key] = valid ? money(entry.amount) : 0
  }
  const totalAssets = money(values.fixed + values.inventory + values.investments + values.other)
  const totalDebt = values.debt, net = money(totalAssets - totalDebt)
  if (![totalAssets, totalDebt, net].every(value => Number.isSafeInteger(Math.round(value * 100)))) blockers.push('totals')
  const ready = !blockers.length
  return { supported, ready, blockers, basis, book, declaration: declaration || null,
    totalAssets: ready ? totalAssets : null, totalDebt: ready ? totalDebt : null,
    netWealth: ready ? net : null, reportedNetWealth: ready ? Math.max(0, net) : null,
    valuationBasis: 'documented_manual', ruleId: 'NO.AS.WEALTH.DOCUMENTED.V1',
    effectiveFor: Number(year), sourceIds: ['NO-TAX-SHARE-WEALTH', 'NO-FSFIN-COMPANY-WEALTH'], officialValidation: 'not_integrated' }
}

global.PlummeTaxWealth = { build: buildTaxWealthValuation, categories: taxWealthCategories };
})(window);
